import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { PageHeader, useToast } from "./components/admin-ui";

interface Subscriber {
  id: string;
  name: string;
  email: string;
  status: string;
}

/**
 * Email Broadcast — lets admins manually send a message to newsletter
 * subscribers and/or custom recipients (typed in bulk), delivered via Resend.
 */
export default function EmailBroadcastPage() {
  const { show } = useToast();

  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loadingSubs, setLoadingSubs] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [customEmails, setCustomEmails] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("newsletter_subscribers")
          .select("id, name, email, status")
          .eq("status", "subscribed")
          .order("created_at", { ascending: false });
        if (error) throw error;
        if (active) setSubscribers((data ?? []) as Subscriber[]);
      } catch {
        if (active) setSubscribers([]);
      } finally {
        if (active) setLoadingSubs(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const parsedCustom = useMemo(() => {
    return customEmails
      .split(/[\n,;]+/)
      .map((e) => e.trim())
      .filter((e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
  }, [customEmails]);

  const recipientCount = selected.size + parsedCustom.length;

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === subscribers.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(subscribers.map((s) => s.id)));
    }
  }

  async function send() {
    if (!subject.trim() || !message.trim()) {
      show("Please enter a subject and a message.", "error");
      return;
    }
    const to = [
      ...subscribers.filter((s) => selected.has(s.id)).map((s) => s.email),
      ...parsedCustom,
    ];
    if (to.length === 0) {
      show("Select at least one recipient.", "error");
      return;
    }

    setSending(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("send-email", {
        body: {
          to,
          subject: subject.trim(),
          html: `<div style="font-family:Arial,sans-serif;max-width:520px;margin:0 auto;padding:24px;color:#1f2937;"><h1 style="font-size:18px;margin:0 0 12px;">L+TV TÉLÉVISION</h1><div style="font-size:14px;line-height:1.6;color:#374151;">${message
            .trim()
            .replace(/\n/g, "<br/>")}</div><p style="font-size:12px;color:#9ca3af;margin-top:24px;">The Television That Connects You — Edea, Cameroon</p></div>`,
        },
      });
      if (error) throw error;
      if (data?.ok) {
        setResult({ ok: true, message: data.message || "Email sent." });
        show(`Sent to ${to.length} recipient(s).`);
      } else {
        setResult({ ok: false, message: data?.error || "Failed to send email." });
        show(data?.error || "Failed to send email.", "error");
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to send email.";
      setResult({ ok: false, message: msg });
      show(msg, "error");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Email Broadcast"
        subtitle="Manually send an email to newsletter subscribers and/or custom recipients."
        actions={
          <button
            type="button"
            onClick={send}
            disabled={sending || recipientCount === 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-send-plane-line text-sm" />
            </span>
            {sending ? "Sending…" : `Send (${recipientCount})`}
          </button>
        }
      />

      {result && (
        <div
          className={`text-sm rounded-md p-3 flex items-start gap-2 ${
            result.ok
              ? "bg-green-50 border border-green-200 text-green-700"
              : "bg-accent-50 border border-accent-200 text-accent-700"
          }`}
        >
          <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
            <i className={`${result.ok ? "ri-checkbox-circle-line" : "ri-error-warning-line"} text-sm`} />
          </span>
          <span>{result.message}</span>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recipients */}
        <div className="bg-white rounded-lg border border-background-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-foreground-900">Recipients</h2>
            <button
              type="button"
              onClick={toggleAll}
              className="text-xs font-medium text-primary-600 hover:underline whitespace-nowrap"
            >
              {selected.size === subscribers.length && subscribers.length > 0
                ? "Deselect all"
                : "Select all"}
            </button>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">
              Newsletter subscribers
            </label>
            {loadingSubs ? (
              <p className="text-sm text-foreground-500">Loading subscribers…</p>
            ) : subscribers.length === 0 ? (
              <p className="text-sm text-foreground-500">
                No subscribers yet. Visitors can subscribe on the homepage.
              </p>
            ) : (
              <div className="max-h-56 overflow-y-auto border border-background-200 rounded-md divide-y divide-background-50">
                {subscribers.map((s) => (
                  <label
                    key={s.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-background-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(s.id)}
                      onChange={() => toggle(s.id)}
                      className="w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-300"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-foreground-900 truncate">{s.name || s.email}</span>
                      <span className="block text-xs text-foreground-500 truncate">{s.email}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">
              Add custom emails (one per line, or comma-separated)
            </label>
            <textarea
              value={customEmails}
              onChange={(e) => setCustomEmails(e.target.value)}
              rows={4}
              placeholder={"person1@example.com\nperson2@example.com"}
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            {parsedCustom.length > 0 && (
              <p className="mt-1 text-xs text-foreground-500">
                {parsedCustom.length} valid custom recipient(s) detected.
              </p>
            )}
          </div>
        </div>

        {/* Compose */}
        <div className="bg-white rounded-lg border border-background-200 p-5">
          <h2 className="text-sm font-semibold text-foreground-900 mb-4">Compose message</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-1.5">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Daily update from L+TV"
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-1.5">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={8}
                placeholder="Write your message…"
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}